import { RevxJobSize } from 'components/RevxJobSize';
import { Col, Divider, Row, Space } from 'antd';
import { RevxDate } from 'components/RevxDate';
import { RevxMoney } from 'components/RevxMoney';
import {
  PrintingPart,
  ShippingItem,
  WorkspaceTabSummaryDTO,
} from 'domains/workspaceDetailDomain';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cs from 'classnames';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { addressTranslator } from 'utils/address';
import './Summary.less';
import { JobItem } from 'domains/workspaceDomain';
import useJobCardInfo from '../../../../hooks/useJobCardInfo';
import { useMount } from 'ahooks';
import { useRole } from 'hooks';

type Props = {
  jobData: JobItem;
  summary?: WorkspaceTabSummaryDTO;
};

const CommonSummary = ({ summary, jobData }: Props) => {
  const [bookletShow, setBookletShow] = useState(true);
  const [shippingShow, setShippingShow] = useState(true);
  const [columns, setColumns] = useState<TableColumnsType<ShippingItem>>([]);

  const { t } = useTranslation();
  const { onlyOneParticipant, hasCustomer, hasProxy } = useJobCardInfo(jobData);
  const { supplierRole } = useRole();

  useMount(() => {
    const filterIndex = ['Shipping Account', 'Arrival Date'];
    const needFilter = onlyOneParticipant && (hasCustomer || hasProxy);
    const widthFactor = needFilter ? 1.5 : 1;
    let columns: TableColumnsType<ShippingItem> = [
      {
        title: 'Address',
        dataIndex: 'address',
        render: (v, r) => {
          return addressTranslator.dataToAddress(r.shippingInfo);
        },
      },
      {
        title: 'Quantity',
        dataIndex: 'itemQuantity',
        width: 100 * widthFactor,
      },
      {
        title: 'Shipping Account',
        dataIndex: ['shippingInfo', 'accountType'],
        width: 150 * widthFactor,
      },
      {
        title: 'Shipping Method',
        dataIndex: ['shippingInfo', 'carrierService'],
        width: 150 * widthFactor,
      },
      {
        title: 'Arrival Date',
        dataIndex: 'arrivalDate',
        width: 150 * widthFactor,
        render: (v) => {
          return <RevxDate dateStr={v} />;
        },
      },
      {
        title: 'Requested Delivery Date',
        dataIndex: 'expectedDeliveryDate',
        width: 150 * widthFactor,
        render: (v) => {
          return <RevxDate dateStr={v} />;
        },
      },
      {
        title: 'Price',
        dataIndex: 'charge',
        width: 100 * widthFactor,
        render: (v, r) => {
          return (
            <span>
              <RevxMoney value={v} />
              {r.billedSeparately && <span>(billed separately)</span>}
            </span>
          );
        },
      },
    ];
    if (needFilter) {
      columns = columns.filter((c) => !filterIndex.includes(c.title as string));
    }
    setColumns(columns);
  });

  if (!summary) return <></>;
  const {
    printing: {
      name,
      charge,
      quantity,
      finishedSize,
      finishedSizeMetric,
      finishedSizePreset,
      finishedSizePresetUnit,
      orientation,
      services,
      parts,
    },
    shipping,
    total,
    tax,
    markup,
  } = summary;
  return (
    <div className="new-summary">
      <div
        className={`new-summary-card-header ${
          !bookletShow ? 'summary-card-header-hidden' : ''
        }`}
        onClick={() => setBookletShow(!bookletShow)}
      >
        <hgroup>
          <i className="allow" />
          Printing
        </hgroup>
        <hgroup>
          <span>
            <RevxMoney value={charge} />
          </span>
        </hgroup>
      </div>

      <div
        className={cs('new-summary-card-container', {
          'summary-card-container-hidden': !bookletShow,
        })}
      >
        <div hidden={!bookletShow}>
          <Row>
            <div className="title bold">
              <Space>
                <span>{name}</span>
              </Space>
              <Divider type="vertical" />
              <Space>
                <span>QTY:</span>
                <span>{quantity}</span>
              </Space>
            </div>
          </Row>
          <Row>
            <Col span={24}>
              <div className="col-container">
                <span>
                  <span className="bold">Size: </span>
                  <span>
                    <RevxJobSize
                      size={finishedSize}
                      sizeMetric={finishedSizeMetric}
                      sizePreset={finishedSizePreset}
                      sizePresetUnit={finishedSizePresetUnit}
                      orientation={orientation}
                    />
                  </span>
                </span>
                <Divider type="vertical" />
                <span>
                  <span className="bold">Orientation: </span>
                  <span className="capitalize">{orientation}</span>
                </span>
                <Divider type="vertical" />
                <span>
                  <span className="bold">Services: </span>
                  <span>
                    {services.length > 0 ? (
                      <>
                        {services.map((service, index) => (
                          <>
                            <span key={service}>{service}</span>
                            {index !== services.length - 1 && ', '}
                          </>
                        ))}
                      </>
                    ) : (
                      <span>PDF Proof - None</span>
                    )}
                  </span>
                </span>
              </div>
            </Col>
          </Row>
          <Row>
            {parts.map((i) => {
              return (
                <Col span={12}>
                  <div className="col-container-title mt-10">{i.name}</div>
                  {(['paper', 'press', 'color'] as (keyof PrintingPart)[]).map(
                    (key) => {
                      return (
                        <div className="col-container">
                          <span className="bold capitalize">{key}: </span>
                          <span>{i[key]}</span>
                        </div>
                      );
                    }
                  )}
                </Col>
              );
            })}
          </Row>
        </div>
      </div>
      <div
        className={cs('new-summary-card-header', {
          'summary-card-header-hidden': !shippingShow,
        })}
        onClick={() => setShippingShow(!shippingShow)}
      >
        <hgroup>
          <i className="allow" />
          <span>Shipping</span>
        </hgroup>
        <hgroup>
          <RevxMoney value={shipping.charge} />
        </hgroup>
      </div>
      <div
        className={`new-summary-card-container shipping ${
          !shippingShow ? 'summary-card-container-hidden' : ''
        }`}
      >
        <div hidden={!shippingShow}>
          <div className="title">
            <span className="bold">Ready date / ship date: </span>
            <span>
              <RevxDate dateStr={shipping.readyDate} isShippingDate={true} />
            </span>
          </div>
          <div className="shipping-table">
            <RevxTable
              className="no-border"
              dataSource={shipping.items}
              columns={columns}
              pagination={false}
              bordered={false}
            />
          </div>
        </div>
      </div>
      {supplierRole && shipping.extraSupplierShippingCharge > 0 && (
        <div className="new-summary-card-header" style={{ cursor: 'default', marginBottom: 20 }}>
          <hgroup>
            <span>Extra Shipping Charge</span>
          </hgroup>
          <hgroup>
            <RevxMoney value={shipping.extraSupplierShippingCharge} />
          </hgroup>
        </div>
      )}
      <div className="total-job-cost">
        <label className="footer-left">
          {t('workspace.detail.summary.totalJobCost')}
        </label>

        <div className="footer-right">
          <div className="other-info mt-20">
            {!!tax && (
              <p>
                <label>{t('workspace.detail.summary.tax')}: </label>
                <span>
                  <RevxMoney value={tax} />
                </span>
              </p>
            )}
            {!!markup && (
              <p>
                <label>{t('workspace.detail.summary.markup')}: </label>
                <span>
                  <RevxMoney value={markup} />
                </span>
              </p>
            )}
          </div>
          <p>
            <label>{t('workspace.detail.summary.total')}: </label>
            <span>
              <RevxMoney
                value={
                  supplierRole && shipping.extraSupplierShippingCharge > 0
                    ? total + shipping.extraSupplierShippingCharge
                    : total
                }
              />
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CommonSummary;
